import { Control, FormState } from 'react-hook-form';
import { FormItem } from './FormBuilder.types';
import { FormInputBuilder } from './FormBuilderInput';
import {
  getFlexBasis,
  getFormBuilderItemMinWidth,
  FormBuilderGroup as Group,
  FormBuilderItem as Item,
} from './FormBuilderLayout';
import { Fragment } from 'react/jsx-runtime';

type FormItemBuilderProps<T> = {
  formItem: FormItem<T>;
  control: Control<T>;
  formState?: FormState<T>;
  size?: number;
  siblingCount?: number;
  formWidth: number;
  disabled?: boolean;
};

export const FormBuilderItem = <T,>({
  formItem,
  control,
  formState,
  formWidth,
  siblingCount = 1,
  disabled,
}: FormItemBuilderProps<T>) => {
  if (!formItem) {
    return <Fragment />;
  }

  if (Array.isArray(formItem)) {
    formItem = {
      type: 'group',
      items: formItem,
    };
  }
  const type = formItem.type;
  const style = {
    flexBasis: getFlexBasis(formItem, siblingCount),
    minWidth: getFormBuilderItemMinWidth(formItem, formWidth),
  };

  switch (formItem.type) {
    case 'input':
      return (
        <Item style={style}>
          <FormInputBuilder
            formInput={formItem}
            control={control}
            disabled={disabled}
          />
        </Item>
      );
    case 'group':
      return (
        <Group
          style={{
            ...style,
            ...(formItem.nowrap ? { flexWrap: 'nowrap' as const } : {}),
          }}
        >
          {formItem.items.map((subItem, subIndex) => (
            <FormBuilderItem
              key={subIndex}
              formItem={subItem}
              control={control}
              formState={formState}
              size={formItem.size}
              siblingCount={formItem.items.length}
              formWidth={formWidth}
              disabled={disabled}
            />
          ))}
        </Group>
      );
    case 'passthrough':
      return <Item style={style}>{formItem.content}</Item>;
    default:
      throw new Error(
        `Unsupported form item type: ${type}. Supported types are: input, row`
      );
  }
};
